/**
 * Terrain Height Debug Mode
 *
 * Visualizes the terrain height texture computed by SurfaceRenderer.
 * Uses a simple fullscreen shader that samples the pre-computed texture
 * and maps height values to a color gradient:
 * - Deep blue: Deep ocean (< -10ft)
 * - Light blue: Shallow water (-10ft to 0ft)
 * - Green: Low land (0ft to 5ft)
 * - Brown/tan: Higher elevation (> 5ft)
 */

import type { GameEventMap } from "../../../core/entity/Entity";
import { on } from "../../../core/entity/handler";
import {
  defineUniformStruct,
  f32,
  mat3x3,
  type UniformInstance,
} from "../../../core/graphics/UniformStruct";
import {
  FullscreenShader,
  type FullscreenShaderConfig,
} from "../../../core/graphics/webgpu/FullscreenShader";
import type { ShaderModule } from "../../../core/graphics/webgpu/ShaderModule";
import { Matrix3 } from "../../../core/graphics/Matrix3";
import { radToDeg } from "../../../core/util/MathUtil";
import { SurfaceRenderer } from "../../surface-rendering/SurfaceRenderer";
import { DEFAULT_DEPTH } from "../../world/terrain/TerrainConstants";

/** Max terrain height for debug visualization gradient (ft) */
const MAX_TERRAIN_HEIGHT = 500;
import { TerrainQuery } from "../../world/terrain/TerrainQuery";
import { TerrainResources } from "../../world/terrain/TerrainResources";
import { DebugRenderMode } from "./DebugRenderMode";

// Margin for render viewport expansion (must match SurfaceRenderer)
const RENDER_VIEWPORT_MARGIN = 0.1;

/**
 * Uniforms for the terrain height debug shader.
 */
const TerrainHeightDebugUniforms = defineUniformStruct("Params", {
  cameraMatrix: mat3x3,
  worldToTexClip: mat3x3,
  screenWidth: f32,
  screenHeight: f32,
});

/**
 * Params module with uniforms and bindings for terrain height visualization.
 */
const terrainHeightDebugParamsModule: ShaderModule = {
  preamble: /*wgsl*/ `
struct Params {
  cameraMatrix: mat3x3<f32>,
  worldToTexClip: mat3x3<f32>,
  screenWidth: f32,
  screenHeight: f32,
}
`,
  bindings: {
    params: { type: "uniform", wgslType: "Params" },
    heightTexture: { type: "texture", sampleType: "unfilterable-float" },
  },
  code: "",
};

/**
 * Main fullscreen shader module for terrain height visualization.
 */
const terrainHeightDebugMainModule: ShaderModule = {
  dependencies: [terrainHeightDebugParamsModule],
  code: /*wgsl*/ `
// Vertex output
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) clipPosition: vec2<f32>,
}

// Fullscreen vertex shader
@vertex
fn vs_main(@location(0) position: vec2<f32>) -> VertexOutput {
  var out: VertexOutput;
  out.position = vec4<f32>(position, 0.0, 1.0);
  out.clipPosition = position;
  return out;
}

// Convert clip position to world position using screen clipToWorld.
fn clipToWorld(clipPos: vec2<f32>) -> vec2<f32> {
  return (params.cameraMatrix * vec3<f32>(clipPos, 1.0)).xy;
}

// World → screen-space texture UV (covers clip[-1,1] of the expanded
// screen-aligned viewport).
fn worldToHeightUV(worldPos: vec2<f32>) -> vec2<f32> {
  let clip = (params.worldToTexClip * vec3<f32>(worldPos, 1.0)).xy;
  return vec2<f32>((clip.x + 1.0) * 0.5, (1.0 - clip.y) * 0.5);
}

// Sample terrain height at world position
fn sampleTerrainHeight(worldPos: vec2<f32>) -> f32 {
  let uv = worldToHeightUV(worldPos);
  let w = i32(params.screenWidth);
  let h = i32(params.screenHeight);
  let tx = clamp(i32(uv.x * params.screenWidth), 0, w - 1);
  let ty = clamp(i32(uv.y * params.screenHeight), 0, h - 1);
  return textureLoad(heightTexture, vec2<i32>(tx, ty), 0).r;
}

// Map height to grayscale (linear from dark to light)
fn heightToColor(height: f32) -> vec3<f32> {
  // Map height range [${DEFAULT_DEPTH}, ${MAX_TERRAIN_HEIGHT}] to [0.1, 0.9] grayscale
  let t = clamp((height - ${DEFAULT_DEPTH}.0) / ${MAX_TERRAIN_HEIGHT - DEFAULT_DEPTH}.0, 0.0, 1.0);
  let gray = mix(0.1, 0.9, t);
  return vec3<f32>(gray, gray, gray);
}

// Fragment shader
@fragment
fn fs_main(in: VertexOutput) -> @location(0) vec4<f32> {
  let worldPos = clipToWorld(in.clipPosition);
  let height = sampleTerrainHeight(worldPos);
  let color = heightToColor(height);
  return vec4<f32>(color, 1.0);
}
`,
};

const terrainHeightDebugShaderConfig: FullscreenShaderConfig = {
  modules: [terrainHeightDebugMainModule],
  label: "TerrainHeightDebugShader",
};

// Convert radians to compass direction
function radiansToCompass(radians: number): string {
  // Normalize to 0-2π
  const normalized = ((radians % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  // Convert to degrees (0° = East, with +Y=down so positive angles rotate clockwise on screen)
  const degrees = (normalized * 180) / Math.PI;
  // Convert to compass bearing (0° = North, clockwise)
  const compassDeg = (degrees + 90) % 360;

  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(compassDeg / 45) % 8;
  return directions[index];
}

function radiansToCompassBearingDeg(radians: number): number {
  const normalized = ((radians % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const degrees = (normalized * 180) / Math.PI;
  return (degrees + 90) % 360;
}

export class TerrainHeightDebugMode extends DebugRenderMode {
  layer = "windViz" as const;

  private shader: FullscreenShader | null = null;
  private uniformBuffer: GPUBuffer | null = null;
  private uniforms: UniformInstance<
    typeof TerrainHeightDebugUniforms.fields
  > | null = null;
  private bindGroup: GPUBindGroup | null = null;
  private initialized = false;

  // Track texture view to detect changes
  private lastTextureView: GPUTextureView | null = null;

  // Placeholder texture for when surface renderer isn't ready
  private placeholderTexture: GPUTexture | null = null;
  private placeholderTextureView: GPUTextureView | null = null;

  // Terrain query for cursor position
  private terrainQuery: TerrainQuery;

  constructor() {
    super();

    // Query terrain at cursor position
    this.terrainQuery = this.addChild(
      new TerrainQuery(() => this.getCursorQueryPoint()),
    );
  }

  private getCursorQueryPoint() {
    if (!this.game) return [];

    const mouseWorldPos = this.game.camera.toWorld(this.game.io.mousePosition);
    if (!mouseWorldPos) return [];

    return [mouseWorldPos];
  }

  @on("add")
  async onAdd() {
    await this.ensureInitialized();
  }

  private async ensureInitialized(): Promise<void> {
    if (this.initialized) return;

    const device = this.game.getWebGPUDevice();

    // Create shader
    this.shader = new FullscreenShader(terrainHeightDebugShaderConfig);
    await this.shader.init();

    // Create uniform buffer
    this.uniformBuffer = device.createBuffer({
      size: TerrainHeightDebugUniforms.byteSize,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      label: "Terrain Height Debug Uniform Buffer",
    });

    // Create uniform instance
    this.uniforms = TerrainHeightDebugUniforms.create();

    // Create placeholder texture (1x1 with default depth value)
    this.placeholderTexture = device.createTexture({
      size: { width: 1, height: 1 },
      format: "r32float",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      label: "Terrain Height Debug Placeholder",
    });
    this.placeholderTextureView = this.placeholderTexture.createView();

    // Write default depth value
    const data = new Float32Array([DEFAULT_DEPTH]);
    device.queue.writeTexture(
      { texture: this.placeholderTexture },
      data,
      { bytesPerRow: 4 },
      { width: 1, height: 1 },
    );

    this.initialized = true;
  }

  @on("render")
  onRender(_event: GameEventMap["render"]): void {
    if (!this.initialized || !this.shader || !this.uniforms) return;

    const renderer = this.game.getRenderer();
    const renderPass = renderer.getCurrentRenderPass();
    if (!renderPass) return;

    const width = renderer.getWidth();
    const height = renderer.getHeight();

    // Build clipToWorld (screen) and texClipToWorld (expanded, same as
    // SurfaceRenderer). worldToTexClip is its inverse.
    const clipToScreen = new Matrix3();
    clipToScreen.translate(width / 2, height / 2);
    clipToScreen.scale(width / 2, height / 2);
    const clipToWorld = this.game.camera.getMatrix().clone().invert();
    clipToWorld.multiply(clipToScreen);

    const texScale = 1 + 2 * RENDER_VIEWPORT_MARGIN;
    const texClipToWorld = clipToWorld.clone().scale(texScale, texScale);
    const worldToTexClip = texClipToWorld.clone().invert();

    // Update uniforms
    this.uniforms.set.cameraMatrix(clipToWorld);
    this.uniforms.set.worldToTexClip(worldToTexClip);
    this.uniforms.set.screenWidth(width);
    this.uniforms.set.screenHeight(height);

    this.uniforms.uploadTo(this.uniformBuffer!);

    // Get terrain height texture from SurfaceRenderer
    const surfaceRenderer = this.game.entities.tryGetSingleton(SurfaceRenderer);
    const textureView =
      surfaceRenderer?.getTerrainHeightTextureView() ??
      this.placeholderTextureView;

    if (!textureView) return;

    // Rebuild bind group if texture changed
    if (this.lastTextureView !== textureView) {
      this.bindGroup = this.shader.createBindGroup({
        params: { buffer: this.uniformBuffer! },
        heightTexture: textureView,
      });
      this.lastTextureView = textureView;
    }

    if (this.bindGroup) {
      this.shader.render(renderPass, this.bindGroup);
    }
  }

  @on("destroy")
  onDestroy(): void {
    this.shader?.destroy();
    this.uniformBuffer?.destroy();
    this.placeholderTexture?.destroy();
  }

  getModeName(): string {
    return "Terrain Height";
  }

  getHudInfo(): string | null {
    const terrainResources =
      this.game.entities.tryGetSingleton(TerrainResources);
    const contourCount = terrainResources?.getContourCount() ?? 0;
    return `Contours: ${contourCount}\nDark=deep (${DEFAULT_DEPTH}ft), Light=high (+${MAX_TERRAIN_HEIGHT}ft)\nCursor: slope angle + grade + downhill direction`;
  }

  getCursorInfo(): string | null {
    const mouseWorldPos = this.game.camera.toWorld(this.game.io.mousePosition);
    if (!mouseWorldPos) return null;

    // Get terrain height at cursor from query
    if (this.terrainQuery.length === 0) return null;

    const result = this.terrainQuery.get(0);
    const height = result.height;
    const normal = result.normal;
    const horizontalComponent = Math.hypot(normal.x, normal.y);
    const nz = Math.sqrt(
      Math.max(0, 1 - horizontalComponent * horizontalComponent),
    );

    // Slope steepness:
    // - angle from horizontal (degrees)
    // - grade as vertical feet gained/lost per 100 horizontal feet
    const slopeAngleDeg = radToDeg(Math.atan2(horizontalComponent, nz));
    const gradePercent =
      nz > 1e-5 ? (horizontalComponent / nz) * 100 : Number.POSITIVE_INFINITY;
    const gradeText = Number.isFinite(gradePercent)
      ? `${gradePercent.toFixed(1)}% grade`
      : "~vertical";

    if (horizontalComponent < 1e-4) {
      return `Terrain Height: ${height.toFixed(1)} ft\nSteepness: ${slopeAngleDeg.toFixed(1)}° (${gradeText})\nDownhill: Flat`;
    }

    const downhillAngle = Math.atan2(normal.y, normal.x);
    const downhillCompass = radiansToCompass(downhillAngle);
    const downhillBearingDeg = radiansToCompassBearingDeg(downhillAngle);

    return `Terrain Height: ${height.toFixed(1)} ft\nSteepness: ${slopeAngleDeg.toFixed(1)}° (${gradeText})\nDownhill: ${downhillCompass} (${downhillBearingDeg.toFixed(0)}°)`;
  }
}
